import { BasePublicApi } from '#packages/apilib';
import { RtlLtrSettingsManagerScope } from './rtlLtrSettingsManagerScope';
import constants from '#packages/constants';
import experiments from 'experiment';
import * as stateManagement from '#packages/stateManagement';
import * as helpIds from '#packages/helpIds';
import * as util from '#packages/util';

const { showUserActionNotification } = stateManagement.notifications.actions;

const { PANELS, USER_PREFS } = constants;

type FlowType = 'ltr_to_rtl' | 'rtl_to_ltr';

function getSiteLangDirection(
  scope: RtlLtrSettingsManagerScope,
  siteLang: string,
) {
  const { editorAPI } = scope;

  const isRTLLang = editorAPI.documentServices.accessibility.isRTL(siteLang);
  return isRTLLang ? 'rtl' : 'ltr';
}

const isSiteDirectionAlignWithLangDirection = (
  scope: RtlLtrSettingsManagerScope,
) => {
  const { editorAPI } = scope;
  const siteLang = editorAPI.documentServices.generalInfo.getRegionalLanguage();
  const isRTLLang = editorAPI.documentServices.accessibility.isRTL(siteLang);
  const siteDirection =
    editorAPI.documentServices.accessibility.getSiteDirection();

  return (
    (siteDirection === 'rtl' && isRTLLang) ||
    (siteDirection === 'ltr' && !isRTLLang)
  );
};

function hasUserAlreadySeenRtlLtrSettingsPanel(
  scope: RtlLtrSettingsManagerScope,
): boolean {
  const { editorAPI } = scope;

  const lastLangDirectionModeWhenRtlLtrSettingPanelOpen =
    stateManagement.userPreferences.selectors.getSiteUserPreferences<boolean>(
      USER_PREFS.RTL_LTR_SETTINGS
        .LAST_LANG_DIRECTION_ON_DIRECTION_SETTINGS_PANEL_OPEN,
    )(editorAPI.store.getState());

  return (
    lastLangDirectionModeWhenRtlLtrSettingPanelOpen ===
    getSiteLangDirection(
      scope,
      editorAPI.documentServices.generalInfo.getRegionalLanguage(),
    )
  );
}

function isNeededToUpdateRtlLtrSettingsPanel(
  scope: RtlLtrSettingsManagerScope,
) {
  if (!experiments.isOpen('se_showAlignSiteDirectionModal')) {
    return false;
  }

  if (isSiteDirectionAlignWithLangDirection(scope)) {
    return false;
  }

  return !hasUserAlreadySeenRtlLtrSettingsPanel(scope);
}

async function setSiteDirection(
  scope: RtlLtrSettingsManagerScope,
  value: 'rtl' | 'ltr',
  options: {
    shouldShowNotification?: boolean;
    biParams: {
      origin: string;
      flow: FlowType;
    };
  },
) {
  const { shouldShowNotification = true, biParams } = options;
  util.fedopsLogger.interactionStarted(
    util.fedopsLogger.INTERACTIONS.SET_OPT_IN_RTL_SUPPORT,
    {
      customParams: { flow: biParams.flow },
    },
  );
  const { editorAPI } = scope;

  editorAPI.documentServices.accessibility.setSiteDirection(value);
  await editorAPI.waitForChangesAppliedAsync();

  if (editorAPI.dsRead.accessibility.autoDomReorder.get()) {
    await editorAPI.documentServices.accessibility.executeDomOrder();
  }

  editorAPI.history.add(`accessibility rtl support change into ${value}`);
  if (shouldShowNotification) {
    editorAPI.store.dispatch(
      showUserActionNotification({
        origin: biParams.origin,
        message: 'Notifications_DOMOrder_directionChange_message',
        type: 'success',
        link: {
          caption: 'Notifications_DOMOrder_directionChange_link',
          onNotificationLinkClick: () => {
            editorAPI.panelManager.openHelpCenter(
              helpIds.HELP_CENTER.SITE_LANGUAGE_AND_DOM,
              null,
              {
                learn_more: true,
                panel_name: 'rtl_ltr_success_action_notification',
                origin: biParams.flow,
              },
            );
          },
        },
      }),
    );
  }
  util.fedopsLogger.interactionEnded(
    util.fedopsLogger.INTERACTIONS.SET_OPT_IN_RTL_SUPPORT,
    {
      customParams: { flow: biParams.flow },
    },
  );
}

export async function updateRtlLtrSettingsPanelIfNeeded(
  scope: RtlLtrSettingsManagerScope,
) {
  const { editorAPI, panelManagerApi } = scope;

  if (isNeededToUpdateRtlLtrSettingsPanel(scope)) {
    const currentSiteDirection =
      editorAPI.documentServices.accessibility.getSiteDirection();
    const flow: FlowType =
      currentSiteDirection === 'rtl' ? 'rtl_to_ltr' : 'ltr_to_rtl';
    const isNewSite = editorAPI.generalInfo.isDraft();
    const shouldShowRtlLtrSettingsPanel = !isNewSite;

    if (!shouldShowRtlLtrSettingsPanel) {
      await setSiteDirection(
        scope,
        currentSiteDirection === 'rtl' ? 'ltr' : 'rtl',
        {
          shouldShowNotification: false,
          biParams: {
            origin: 'auto_update_rtl_ltr_settings',
            flow,
          },
        },
      );
      return;
    }

    panelManagerApi.openPanel(
      PANELS.RTL_LTR_SETTINGS.CHANGE_RTL_LTR_SETTINGS_PANEL,
      {
        flow,
        onApprove: async () =>
          await setSiteDirection(
            scope,
            currentSiteDirection === 'rtl' ? 'ltr' : 'rtl',
            {
              shouldShowNotification: true,
              biParams: {
                origin: PANELS.RTL_LTR_SETTINGS.CHANGE_RTL_LTR_SETTINGS_PANEL,
                flow,
              },
            },
          ),
        onClose: () => {
          editorAPI.store.dispatch(
            stateManagement.userPreferences.actions.setSiteUserPreferences(
              USER_PREFS.RTL_LTR_SETTINGS
                .LAST_LANG_DIRECTION_ON_DIRECTION_SETTINGS_PANEL_OPEN,
              getSiteLangDirection(
                scope,
                editorAPI.documentServices.generalInfo.getRegionalLanguage(),
              ),
            ),
          );
        },
      },
    );
  }
}

async function toggleSiteDirection(
  scope: RtlLtrSettingsManagerScope,
  options: {
    shouldShowNotification?: boolean;
    biParams: {
      origin: string;
    };
  },
) {
  const { editorAPI } = scope;
  const currentDirection =
    editorAPI.documentServices.accessibility.getSiteDirection();
  const newDirection = currentDirection === 'rtl' ? 'ltr' : 'rtl';
  const flow: FlowType = newDirection === 'rtl' ? 'ltr_to_rtl' : 'rtl_to_ltr';

  await setSiteDirection(scope, newDirection, {
    shouldShowNotification: options.shouldShowNotification,
    biParams: {
      origin: options.biParams.origin,
      flow,
    },
  });
}

export class RtlLtrSettingsManagerApi extends BasePublicApi<RtlLtrSettingsManagerScope> {
  getSiteLangDirection = this.bindScope(getSiteLangDirection);
  isNeededToUpdateRtlLtrSettingsPanel = this.bindScope(
    isNeededToUpdateRtlLtrSettingsPanel,
  );
  isSiteDirectionAlignWithLangDirection = this.bindScope(
    isSiteDirectionAlignWithLangDirection,
  );
  toggleSiteDirection = this.bindScope(toggleSiteDirection);
  setSiteDirection = this.bindScope(setSiteDirection);
  updateRtlLtrSettingsPanelIfNeeded = this.bindScope(
    updateRtlLtrSettingsPanelIfNeeded,
  );
}
